import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hojeNoFuso } from '@arena/shared/tempo';
import {
  CATEGORIA_PRODUTO,
  margemPorExtenso,
  type EntradaDeMercadoria,
  type CategoriaProduto,
  type ProdutoNaTabela,
} from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { invalidarDinheiro } from '../api/invalidacoes.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, BotaoDeIcone, Campo, Escolhas, Formulario, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  FORMAS_DA_COMPRA,
  avisoDoVencimento,
  textoDaEntradaRegistrada,
  vencimentoParaEnviar,
} from './entrada.js';
import {
  LINHAS_MAXIMAS_DA_ENTRADA,
  NOME_DO_PRODUTO,
  NOTA_MAXIMA,
  escolherProdutoDaLinha,
  itemDaLinha,
  linhaVazia,
  margemDaLinha,
  problemasDaLinha,
  totalDaEntrada,
  totalDaLinha,
  trocarModoDaLinha,
  veOCusto,
  type LinhaDaEntrada,
  type ModoDaLinha,
} from './formatos.js';

/**
 * `#m-produto` — "Entrada de mercadoria".
 *
 * Uma compra, um fornecedor, uma nota e VÁRIOS produtos: cada linha é um
 * produto já cadastrado ou um produto novo, e quem repõe é quem descobre que
 * o fornecedor reajustou — então preço e estoque mínimo se corrigem na mesma
 * linha, sem sair para outra tela. A entrada inteira vai numa chamada só, e o
 * produto novo nasce no servidor, na mesma transação: se a entrada falhar,
 * nenhum produto fica órfão no catálogo.
 *
 * O rodapé mostra a saída financeira ANTES de gravar — a soma de `custoDaEntrada`
 * de `@arena/shared`, a mesma conta do servidor. A margem de cada linha só
 * aparece para quem recebe o custo dos produtos, e é a do CUSTO MÉDIO que o
 * produto terá depois da entrada, não a do preço desta compra.
 *
 * Os campos da linha começam vazios e, ao escolher o produto, trazem os
 * valores DELE. O servidor aplica preço e mínimo sempre que chegam; por isso
 * a entrada só os envia quando a pessoa os mudou — medido contra o retrato do
 * produto na hora da escolha, não contra a consulta viva (ver `formatos.ts`).
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */

const MODOS: readonly { valor: ModoDaLinha; rotulo: string }[] = [
  { valor: 'existente', rotulo: 'Produto cadastrado' },
  { valor: 'novo', rotulo: 'Produto novo' },
];

export function ModalDeEntrada({ onFechar }: { onFechar: () => void }) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const { eu } = useSessao();

  const [linhas, setLinhas] = useState<LinhaDaEntrada[]>(() => [linhaVazia(1)]);
  const proximaChave = useRef(2);
  const [fornecedorId, setFornecedorId] = useState('');
  // `null` = o select; texto = o fornecedor que está sendo cadastrado.
  const [novoFornecedor, setNovoFornecedor] = useState<string | null>(null);
  const [nota, setNota] = useState('');
  const [formaPagamento, setFormaPagamento] =
    useState<EntradaDeMercadoria['formaPagamento']>('Pix');
  // Vazio = pago hoje. O servidor é quem decide, com o dia da arena; o dia do
  // navegador só serve ao aviso sob o campo.
  const [vencimento, setVencimento] = useState('');
  const hoje = hojeNoFuso(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [erro, setErro] = useState<string | undefined>();

  const produtos = useQuery({
    queryKey: ['estoque', 'produtos'],
    queryFn: () => estoqueApi.produtos(),
  });
  const fornecedores = useQuery({
    queryKey: ['estoque', 'fornecedores'],
    queryFn: estoqueApi.fornecedores,
  });

  const tabela = produtos.data ?? [];
  // O servidor só manda o custo a quem pode vê-lo: a tabela vem inteira com
  // ele ou inteira sem. Sem tabela (a primeira entrada de uma arena), a
  // projeção da sessão desempata.
  const vejoOCusto = veOCusto(tabela, eu?.permissoes.estoque?.projecao);

  const prontas = linhas.flatMap((linha) => {
    const item = itemDaLinha(linha);
    return item ? [item] : [];
  });
  const total = totalDaEntrada(linhas);
  const novos = linhas.filter((linha) => linha.modo === 'novo').length;
  // O campo "Cadastrar fornecedor" aberto esconde o select: registrar agora
  // usaria o fornecedor que estava escolhido, sem a pessoa ver qual é. Então o
  // botão espera o cadastro (que fecha o campo e escolhe o novo) ou o "Voltar".
  const cadastrandoFornecedor = novoFornecedor !== null;
  const fornecedorSemGravar = novoFornecedor !== null && novoFornecedor.trim() !== '';
  const pronto = fornecedorId !== '' && !cadastrandoFornecedor && prontas.length === linhas.length;

  // A compra paga cria uma saída em Fornecedores: o razão e, se foi em
  // dinheiro com caixa aberto, o esperado do caixa mudam junto com o estoque.
  const invalidar = () =>
    Promise.all([
      cliente.invalidateQueries({ queryKey: ['estoque'] }),
      invalidarDinheiro(cliente),
    ]);

  const mudar = (chave: number, ajuste: (linha: LinhaDaEntrada) => LinhaDaEntrada) =>
    setLinhas((atuais) => atuais.map((l) => (l.chave === chave ? ajuste(l) : l)));

  const adicionar = () => {
    const chave = proximaChave.current;
    proximaChave.current += 1;
    setLinhas((atuais) => [...atuais, linhaVazia(chave)]);
  };

  const remover = (chave: number) =>
    setLinhas((atuais) => (atuais.length > 1 ? atuais.filter((l) => l.chave !== chave) : atuais));

  const registrar = useMutation({
    mutationFn: () =>
      estoqueApi.registrarEntrada({
        fornecedorId,
        ...(nota.trim() ? { nota: nota.trim() } : {}),
        formaPagamento,
        ...vencimentoParaEnviar(vencimento),
        itens: prontas,
      }),
    // O erro da tentativa anterior sai da tela quando começa a seguinte.
    onMutate: () => setErro(undefined),
    onSuccess: async (resultado) => {
      await invalidar();
      const cadastrados =
        novos === 0
          ? ''
          : ` ${novos} ${novos === 1 ? 'produto novo cadastrado' : 'produtos novos cadastrados'}.`;
      avisar(textoDaEntradaRegistrada(resultado, vencimento, cadastrados));
      onFechar();
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  const criarFornecedor = useMutation({
    mutationFn: (nome: string) => estoqueApi.criarFornecedor({ nome }),
    onSuccess: async (criado) => {
      await cliente.invalidateQueries({ queryKey: ['estoque', 'fornecedores'] });
      setFornecedorId(criado.id);
      setNovoFornecedor(null);
    },
    onError: (e: unknown) => setErro(mensagemDoErro(e)),
  });

  const cadastrarFornecedor = () => {
    if (novoFornecedor === null || novoFornecedor.trim().length < 2) return;
    if (criarFornecedor.isPending) return; // Enter repetido não manda um segundo POST
    setErro(undefined);
    criarFornecedor.mutate(novoFornecedor.trim());
  };

  // Um produto só pode estar em uma linha: o servidor recusa o repetido, e a
  // tela nem o oferece.
  const escolhidos = new Set(linhas.map((l) => l.produtoId).filter(Boolean));

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Entrada de mercadoria"
      descricao="Gera a saída financeira correspondente ao fornecedor."
      rodape={
        <>
          <span className="left">
            Total da entrada{' '}
            <b className="num" style={{ color: 'var(--txt)', fontSize: 15 }}>
              {dinheiro(total)}
            </b>{' '}
            · {linhas.length} {linhas.length === 1 ? 'produto' : 'produtos'}
          </span>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => registrar.mutate()} disabled={!pronto || registrar.isPending}>
            {registrar.isPending ? 'Registrando…' : 'Registrar entrada'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        {erro && <Nota>{erro}</Nota>}
        {produtos.isError && <Nota>{mensagemDoErro(produtos.error)}</Nota>}

        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo
            rotulo="Fornecedor"
            erro={
              fornecedorSemGravar
                ? 'Cadastre o fornecedor (botão Cadastrar) ou volte à lista para registrar a entrada.'
                : undefined
            }
          >
            {novoFornecedor === null ? (
              <select
                value={fornecedorId}
                aria-label="Fornecedor"
                onChange={(e) => {
                  if (e.target.value === '__novo') setNovoFornecedor('');
                  else setFornecedorId(e.target.value);
                }}
              >
                <option value="">Escolha…</option>
                {fornecedores.data?.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
                <option value="__novo">+ Cadastrar fornecedor…</option>
              </select>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  autoFocus
                  value={novoFornecedor}
                  aria-label="Nome do novo fornecedor"
                  onChange={(e) => setNovoFornecedor(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    cadastrarFornecedor();
                  }}
                  placeholder="Distribuidora Centro-Oeste"
                />
                <Botao
                  pequeno
                  variante="contorno"
                  onClick={cadastrarFornecedor}
                  disabled={novoFornecedor.trim().length < 2 || criarFornecedor.isPending}
                >
                  Cadastrar
                </Botao>
                <Botao variante="fantasma" pequeno onClick={() => setNovoFornecedor(null)}>
                  Voltar
                </Botao>
              </div>
            )}
          </Campo>

          <Campo rotulo="Nota fiscal (opcional)">
            <input
              value={nota}
              maxLength={NOTA_MAXIMA}
              aria-label="Nota fiscal"
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ex.: NF 4821"
            />
          </Campo>

          <Campo rotulo="Forma de pagamento">
            <select
              value={formaPagamento}
              aria-label="Forma de pagamento"
              onChange={(e) =>
                setFormaPagamento(e.target.value as EntradaDeMercadoria['formaPagamento'])
              }
            >
              {FORMAS_DA_COMPRA.map((forma) => (
                <option key={forma.valor} value={forma.valor}>
                  {forma.rotulo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            rotulo="Vencimento (opcional)"
            ajuda={avisoDoVencimento(vencimento, hoje) ?? 'Em branco: a compra já foi paga hoje.'}
          >
            <input
              type="date"
              value={vencimento}
              aria-label="Vencimento"
              onChange={(e) => setVencimento(e.target.value)}
            />
          </Campo>
        </Formulario>

        {linhas.map((linha, indice) => (
          <LinhaDoProduto
            key={linha.chave}
            numero={indice + 1}
            linha={linha}
            produtos={tabela.filter((p) => p.id === linha.produtoId || !escolhidos.has(p.id))}
            vejoOCusto={vejoOCusto}
            podeRemover={linhas.length > 1}
            onMudar={(ajuste) => mudar(linha.chave, ajuste)}
            onRemover={() => remover(linha.chave)}
          />
        ))}

        <div>
          <Botao
            variante="contorno"
            pequeno
            icone="plus"
            onClick={adicionar}
            disabled={linhas.length >= LINHAS_MAXIMAS_DA_ENTRADA}
          >
            Adicionar produto
          </Botao>
        </div>

        <Nota>
          A entrada gera <b>uma</b> saída de <b>{dinheiro(total)}</b> no financeiro, categoria
          Fornecedores, centro de custo Bar — a soma de todos os produtos.
        </Nota>
      </CorpoDeModal>
    </Modal>
  );
}

/**
 * Uma linha da entrada: o produto (cadastrado ou novo), a quantidade, o custo
 * desta compra, e o preço e o mínimo que ele terá depois dela.
 */
function LinhaDoProduto({
  numero,
  linha,
  produtos,
  vejoOCusto,
  podeRemover,
  onMudar,
  onRemover,
}: {
  numero: number;
  linha: LinhaDaEntrada;
  produtos: readonly ProdutoNaTabela[];
  vejoOCusto: boolean;
  podeRemover: boolean;
  onMudar: (ajuste: (linha: LinhaDaEntrada) => LinhaDaEntrada) => void;
  onRemover: () => void;
}) {
  const problemas = problemasDaLinha(linha);
  const margem = margemDaLinha(linha, vejoOCusto);
  const total = totalDaLinha(linha);
  const campo = (nome: 'nome' | 'quantidade' | 'custo' | 'preco' | 'minimo', valor: string) =>
    onMudar((atual) => ({ ...atual, [nome]: valor }));

  return (
    <section
      aria-label={`Linha ${numero} da entrada`}
      style={{
        display: 'grid',
        gap: 12,
        padding: 14,
        background: 'var(--panel-2)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <Escolhas
          aria-label={`Produto ${numero}: cadastrado ou novo`}
          valor={linha.modo}
          onChange={(modo) => onMudar((atual) => trocarModoDaLinha(atual, modo))}
          opcoes={MODOS}
        />
        <BotaoDeIcone
          icone="x"
          aria-label={`Remover o produto ${numero}`}
          onClick={onRemover}
          disabled={!podeRemover}
        />
      </div>

      <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
        {linha.modo === 'novo' ? (
          <>
            <Campo rotulo="Nome" erro={problemas.nome}>
              <input
                value={linha.nome}
                maxLength={NOME_DO_PRODUTO.maximo}
                aria-label={`Nome do produto novo ${numero}`}
                onChange={(e) => campo('nome', e.target.value)}
                placeholder="Ex.: Coca-Cola Zero 350 ml"
              />
            </Campo>
            <Campo rotulo="Categoria">
              <select
                value={linha.categoria}
                aria-label={`Categoria do produto ${numero}`}
                onChange={(e) =>
                  onMudar((atual) => ({ ...atual, categoria: e.target.value as CategoriaProduto }))
                }
              >
                {CATEGORIA_PRODUTO.map((opcao) => (
                  <option key={opcao} value={opcao}>
                    {opcao}
                  </option>
                ))}
              </select>
            </Campo>
          </>
        ) : (
          <Campo rotulo="Produto">
            <select
              value={linha.produtoId}
              aria-label={`Produto ${numero}`}
              onChange={(e) =>
                onMudar((atual) =>
                  escolherProdutoDaLinha(
                    atual,
                    produtos.find((p) => p.id === e.target.value),
                  ),
                )
              }
            >
              <option value="">Escolha…</option>
              {produtos.map((produto) => (
                <option key={produto.id} value={produto.id}>
                  {produto.nome} · {produto.saldo} un
                </option>
              ))}
            </select>
          </Campo>
        )}

        <Campo rotulo="Quantidade" erro={problemas.quantidade}>
          <input
            type="number"
            min={1}
            value={linha.quantidade}
            aria-label={`Quantidade do produto ${numero}`}
            onChange={(e) => campo('quantidade', e.target.value)}
          />
        </Campo>
        <Campo rotulo="Custo unitário desta compra" erro={problemas.custo}>
          <input
            value={linha.custo}
            inputMode="decimal"
            aria-label={`Custo unitário do produto ${numero}`}
            onChange={(e) => campo('custo', e.target.value)}
          />
        </Campo>
        <Campo rotulo="Preço de venda" erro={problemas.preco}>
          <input
            value={linha.preco}
            inputMode="decimal"
            aria-label={`Preço de venda do produto ${numero}`}
            onChange={(e) => campo('preco', e.target.value)}
          />
        </Campo>
        <Campo rotulo="Estoque mínimo" erro={problemas.minimo}>
          <input
            type="number"
            min={0}
            value={linha.minimo}
            aria-label={`Estoque mínimo do produto ${numero}`}
            onChange={(e) => campo('minimo', e.target.value)}
          />
        </Campo>
      </Formulario>

      {(total > 0 || margem) && (
        <small style={{ color: 'var(--dim)', fontSize: 12 }}>
          {total > 0 && (
            <>
              Esta linha custa <b style={{ color: 'var(--txt)' }}>{dinheiro(total)}</b>.{' '}
            </>
          )}
          {margem && (
            <>
              Margem de <b style={{ color: 'var(--txt)' }}>{margemPorExtenso(margem.margem)}</b>{' '}
              {margem.base === 'custo médio'
                ? `sobre o custo médio (${dinheiro(margem.custo)} depois desta entrada).`
                : 'sobre o custo da compra.'}
            </>
          )}
        </small>
      )}
    </section>
  );
}
